import React, { memo, useCallback, useEffect, useRef } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { useSupportPanel } from 'Context/Support';
import { Spinner } from 'Components/Spinner';

import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';

import classes from './conversationChat.module.css';

function ConversationChat() {
  const { activeConversation, activeConversationId, loading, sendingMessage, sendMessage, markAsRead } =
    useSupportPanel();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mark as read on mount
  useEffect(() => {
    if (activeConversationId) {
      markAsRead(activeConversationId);
    }
  }, [activeConversationId, markAsRead]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversation?.messages]);

  const handleSend = useCallback(
    (body: string) => {
      if (activeConversationId) {
        sendMessage(activeConversationId, body);
      }
    },
    [activeConversationId, sendMessage]
  );

  if (loading && !activeConversation) {
    return (
      <div className={classes.loadingContainer}>
        <Spinner loading />
      </div>
    );
  }

  if (!activeConversation) return null;

  const { conversation, messages } = activeConversation;

  return (
    <div className={classes.container}>
      <div className={classes.chatHeader}>
        <div className={classes.chatSubject}>{conversation.subject}</div>
        <div className={classes.chatStatus}>Status: {conversation.status}</div>
      </div>
      <div className={classes.messages}>
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      <MessageInput onSend={handleSend} disabled={sendingMessage} />
    </div>
  );
}

const ConversationChatMemo = memo(ConversationChat, areEqual);

export { ConversationChatMemo as ConversationChat };
